/**
 * Typed wrappers for the WFH module (migration 063). Paths + shapes are taken
 * VERBATIM from backend/internal/router/router.go and handlers/wfh.go.
 *
 * A distinct /wfh/ prefix (NOT /leave/wfh/) keeps the Go 1.22 mux conflict-free
 * (locked P0). WFH is leave-shaped but BALANCE-FREE; an approved range overlays
 * attendance (a WFH day reads 'wfh' not 'absent') server-side.
 *
 * Cookie-only auth + CSRF + 401→refresh are handled by the shared axios
 * instance. /wfh/* is fenced by RequireFeature("leave_management") server-side.
 */

import { api } from "./client";
import type {
  CreateWFHRequest,
  ListWFHResponse,
  WFHDecisionRequest,
  WFHRequest,
} from "@/types/wfh";

export const WFH_BASE = "/api/v1/wfh";

// GET /wfh/requests[?status=&scope=self][&exclude_self=1] — role-filtered
// in SQL. `exclude_self=1` narrows an approver's list to rows they can
// ACT on (own requests removed).
export async function listWFHRequests(
  params?: { status?: string; scope?: "self"; exclude_self?: boolean },
  signal?: AbortSignal,
): Promise<ListWFHResponse> {
  const { exclude_self, ...rest } = params ?? {};
  const { data } = await api.get<ListWFHResponse>(`${WFH_BASE}/requests`, {
    params: { ...rest, ...(exclude_self ? { exclude_self: 1 } : {}) },
    signal,
  });
  return data;
}

// GET /wfh/requests/{id} — own / manager-of / HR.
export async function getWFHRequest(
  id: number,
  signal?: AbortSignal,
): Promise<WFHRequest> {
  const { data } = await api.get<WFHRequest>(`${WFH_BASE}/requests/${id}`, {
    signal,
  });
  return data;
}

// POST /wfh/requests — own; server computes total_days. No balance impact.
export async function createWFHRequest(
  body: CreateWFHRequest,
): Promise<WFHRequest> {
  const { data } = await api.post<WFHRequest>(`${WFH_BASE}/requests`, body);
  return data;
}

// POST /wfh/requests/{id}/approve — manager-of / HR. CSRF + Audit.
export async function approveWFHRequest(
  id: number,
  body?: WFHDecisionRequest,
): Promise<WFHRequest> {
  const { data } = await api.post<WFHRequest>(
    `${WFH_BASE}/requests/${id}/approve`,
    body ?? {},
  );
  return data;
}

// POST /wfh/requests/{id}/reject — manager-of / HR. CSRF + Audit.
export async function rejectWFHRequest(
  id: number,
  body?: WFHDecisionRequest,
): Promise<WFHRequest> {
  const { data } = await api.post<WFHRequest>(
    `${WFH_BASE}/requests/${id}/reject`,
    body ?? {},
  );
  return data;
}

// POST /wfh/requests/{id}/cancel — requester's own. CSRF + Audit.
export async function cancelWFHRequest(id: number): Promise<WFHRequest> {
  const { data } = await api.post<WFHRequest>(
    `${WFH_BASE}/requests/${id}/cancel`,
    {},
  );
  return data;
}
